import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';

interface Tab {
  title: string;
  content: any;
  id: number;
}

interface TabbedContainerProps {
  tabs: Tab[];
  className?: string;
  ariaLabel?: string;
}

const TabbedContainer = forwardRef(
  (
    {
      tabs,
      className = '',
      ariaLabel = 'Ballot sections',
    }: TabbedContainerProps,
    ref,
  ) => {
    const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

    useImperativeHandle(ref, () => ({
      selectTab(tabTitle: string) {
        const currTab = tabs.find((tab) => tab.title === tabTitle);
        if (currTab) {
          setActiveTab(currTab);
        }
      },
    }));

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      const currentIndex = tabs.findIndex((tab) => tab.id === activeTab.id);
      let nextIndex: number | null = null;
      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      }
      if (nextIndex === null) return;
      event.preventDefault();
      setActiveTab(tabs[nextIndex]);
      tabRefs.current[nextIndex]?.focus();
    };

    return (
      <div className={`tab-card card ${className}`}>
        <div className="head-container">
          <div id="mobile-header">
            <label htmlFor="tab-select" className="screen-reader">
              {ariaLabel}
            </label>
            <select
              id="tab-select"
              className="mobile-tab-select"
              value={String(activeTab.id)}
              onChange={(e) => {
                const selected = tabs.find(
                  (tab) => String(tab.id) === e.target.value,
                );
                if (selected) setActiveTab(selected);
              }}
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={String(tab.id)}>
                  {tab.title}
                </option>
              ))}
            </select>
          </div>

          <div
            id="full-header"
            role="tablist"
            aria-label={ariaLabel}
            onKeyDown={handleKeyDown}
          >
            {tabs.map((tab, index) => (
              <button
                key={tab.title}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
                role="tab"
                id={`tab-${tab.id}`}
                type="button"
                aria-selected={tab.id === activeTab.id}
                aria-controls={`panel-${tab.id}`}
                tabIndex={tab.id === activeTab.id ? 0 : -1}
                className={`tab-card-header card-header ${
                  tab.id === activeTab.id ? 'active' : 'inactive'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                <span className="card-header-title">{tab.title}</span>
              </button>
            ))}
          </div>
        </div>

        <div
          role="tabpanel"
          id={`panel-${activeTab.id}`}
          aria-labelledby={`tab-${activeTab.id}`}
          className="body-container"
          tabIndex={0}
        >
          {activeTab ? activeTab.content : null}
        </div>
      </div>
    );
  },
);

export default TabbedContainer;
